import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Search, MapPin, Clock, Zap, Check, X, Info,
    ArrowRight, ChevronDown, Sparkles,
} from 'lucide-react';

/* ─── Tag library (mirrors GET /tags/suggest) ─── */
const TAG_LIBRARY = [
    { slug: 'moving-furniture',  label: 'moving furniture',  count: 412 },
    { slug: 'lifting-heavy',     label: 'lifting heavy',      count: 298 },
    { slug: 'gardening',         label: 'gardening',          count: 187 },
    { slug: 'dog-walking',       label: 'dog walking',        count: 521 },
    { slug: 'cycling-delivery',  label: 'cycling delivery',   count: 264 },
    { slug: 'play-football',     label: 'play football',      count: 89  },
    { slug: 'photography',       label: 'photography',        count: 156 },
    { slug: 'video-editing',     label: 'video editing',      count: 92  },
    { slug: 'baking',            label: 'baking',             count: 78  },
    { slug: 'graphic-design',    label: 'graphic design',     count: 134 },
    { slug: 'bar-shift',         label: 'bar shift',          count: 389 },
    { slug: 'waiting-tables',    label: 'waiting tables',     count: 412 },
    { slug: 'kitchen-prep',      label: 'kitchen prep',       count: 203 },
    { slug: 'barista',           label: 'barista',            count: 167 },
    { slug: 'deep-clean',        label: 'deep clean',         count: 487 },
    { slug: 'ironing',           label: 'ironing',            count: 89  },
    { slug: 'flat-pack-assembly', label: 'flat-pack assembly', count: 312 },
    { slug: 'babysitting',       label: 'babysitting',        count: 298 },
    { slug: 'tutoring',          label: 'tutoring',           count: 178 },
    { slug: 'cat-sitting',       label: 'cat sitting',        count: 145 },
    { slug: 'event-help',        label: 'event help',         count: 234 },
    { slug: 'wedding-staff',     label: 'wedding staff',      count: 87  },
    { slug: 'promo-staff',       label: 'promo staff',        count: 112 },
    { slug: 'queue-for-me',      label: 'queue for me',       count: 23  },
    { slug: 'drive-me-airport',  label: 'drive me airport',   count: 56  },
];

const BENCHMARKS = {
    'moving-furniture': { min: 30, max: 80 },
    'lifting-heavy': { min: 30, max: 80 },
    'deep-clean': { min: 60, max: 100 },
    'flat-pack-assembly': { min: 40, max: 90 },
    'dog-walking': { min: 15, max: 35 },
    'babysitting': { min: 40, max: 80 },
    'bar-shift': { min: 80, max: 140 },
    'waiting-tables': { min: 80, max: 140 },
    'barista': { min: 60, max: 100 },
    'photography': { min: 80, max: 250 },
    'tutoring': { min: 30, max: 80 },
    'event-help': { min: 60, max: 120 },
};

const TITLE_SUGGESTIONS = [
    'Help moving a 3-seater sofa, 2 floors up',
    'Weekend bar shift — busy gastropub',
    'Walk my dog while I\'m at the dentist',
    'Flat-pack furniture assembly, IKEA wardrobe',
    'Deep clean of 1-bed flat before checkout',
];

function suggestTags(query, exclude = []) {
    const q = (query || '').toLowerCase().trim();
    const ex = new Set(exclude);
    const pool = TAG_LIBRARY.filter(t => !ex.has(t.slug));
    if (!q) return [...pool].sort((a, b) => b.count - a.count).slice(0, 6);
    return pool
        .map(t => {
            const label = t.label.toLowerCase();
            let score = 0;
            if (label === q) score = 1000;
            else if (label.startsWith(q)) score = 500 + t.count / 10;
            else if (label.includes(q)) score = 200 + t.count / 20;
            else if (label.split(/\s|-/).some(w => w.startsWith(q))) score = 100 + t.count / 30;
            return { t, score };
        })
        .filter(x => x.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 6)
        .map(x => x.t);
}

/* ─── Sub-components ─── */

function Field({ label, sub, children, optional }) {
    return (
        <div className="cj-field">
            <div className="cj-field-label">
                <span>{label}</span>
                {optional && <span className="cj-field-optional mono">Optional</span>}
            </div>
            {sub && <div className="cj-field-sub">{sub}</div>}
            {children}
        </div>
    );
}

function CJInput({ icon: Icon, ...rest }) {
    return (
        <div className="cj-input">
            {Icon && <Icon size={16} color="var(--ink-400)" />}
            <input {...rest} />
        </div>
    );
}

function TagInput({ tags, onChange }) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const inputRef = useRef(null);
    const wrapRef = useRef(null);

    const exclude = tags.map(t => t.slug);
    const suggestions = suggestTags(query, exclude);
    const trimmed = query.trim();
    const exactMatch = suggestions.find(s => s.label === trimmed.toLowerCase());
    const canCreate = trimmed.length >= 2 && !exactMatch;
    const items = canCreate
        ? [...suggestions, { __create: true, slug: `_new_${trimmed}`, label: trimmed }]
        : suggestions;

    useEffect(() => {
        const handle = e => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, []);

    useEffect(() => { setActiveIdx(0); }, [query]);

    const addTag = tag => {
        if (tag.__create) {
            const slug = trimmed.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
            onChange([...tags, { slug, label: trimmed, isNew: true }]);
        } else {
            onChange([...tags, tag]);
        }
        setQuery('');
        inputRef.current?.focus();
    };

    const removeTag = slug => onChange(tags.filter(t => t.slug !== slug));

    const onKeyDown = e => {
        if (e.key === 'Enter') { e.preventDefault(); if (items[activeIdx]) addTag(items[activeIdx]); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, items.length - 1)); setOpen(true); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
        else if (e.key === 'Backspace' && !query && tags.length) removeTag(tags[tags.length - 1].slug);
        else if (e.key === 'Escape') setOpen(false);
    };

    return (
        <div className="cj-tags" ref={wrapRef}>
            <div
                className={'cj-tags-input' + (open ? ' is-open' : '')}
                onClick={() => inputRef.current?.focus()}
            >
                {tags.map(t => (
                    <span key={t.slug} className="cj-tag-chip">
                        {t.isNew && <Sparkles size={10} />}
                        <span>{t.label}</span>
                        <button
                            className="cj-tag-x"
                            onClick={e => { e.stopPropagation(); removeTag(t.slug); }}
                            aria-label={`Remove ${t.label}`}
                        >
                            <X size={10} />
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    className="cj-tag-input"
                    placeholder={tags.length === 0 ? 'play football, deep clean, dog walking…' : 'Add another'}
                    value={query}
                    onChange={e => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                />
            </div>

            {open && items.length > 0 && (
                <div className="cj-tags-pop">
                    <div className="cj-tags-pop-head mono">
                        {!query ? 'Trending tags' : `Matches for "${query}"`}
                    </div>
                    <ul className="cj-tags-list">
                        {items.map((it, i) => (
                            <li
                                key={it.slug}
                                className={'cj-tags-item' + (i === activeIdx ? ' is-active' : '')}
                                onMouseEnter={() => setActiveIdx(i)}
                                onMouseDown={e => { e.preventDefault(); addTag(it); }}
                            >
                                {it.__create ? (
                                    <>
                                        <span className="cj-tags-item-icon"><Sparkles size={11} /></span>
                                        <span>Create <strong>"{it.label}"</strong></span>
                                        <span className="cj-tags-item-meta mono">New tag</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="cj-tags-item-icon">#</span>
                                        <span>{it.label}</span>
                                        <span className="cj-tags-item-meta mono">{it.count.toLocaleString()} jobs</span>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                    <div className="cj-tags-pop-foot">
                        <kbd>↑↓</kbd> navigate · <kbd>↵</kbd> add · <kbd>⌫</kbd> remove last
                    </div>
                </div>
            )}

            {tags.length === 0 && !open && (
                <div className="cj-tags-starter">
                    <span className="mono cj-suggest-label">Popular:</span>
                    {suggestTags('', [], 5).map(t => (
                        <button key={t.slug} className="cj-suggest-chip" onClick={() => addTag(t)}>
                            {t.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function Switch({ on, onChange }) {
    return (
        <button
            type="button"
            className={'ix-switch' + (on ? ' is-on' : '')}
            onClick={() => onChange(!on)}
            aria-pressed={on}
        >
            <span className="ix-switch-thumb" />
        </button>
    );
}

/* ─── Step components ─── */

function Step1({ data, set }) {
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(false);

    useEffect(() => {
        axios.get('https://countriesnow.space/api/v0.1/countries/iso')
            .then(r => setCountries((r.data?.data ?? []).map(c => c.name)))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!data.country) { setCities([]); return; }
        setCitiesLoading(true);
        axios.post('https://countriesnow.space/api/v0.1/countries/cities', { country: data.country })
            .then(r => setCities(r.data?.data ?? []))
            .catch(() => setCities([]))
            .finally(() => setCitiesLoading(false));
    }, [data.country]);

    return (
        <div className="cj-step">
            <Field label="Job title" sub="Be specific. People scroll fast.">
                <CJInput
                    icon={Search}
                    placeholder="e.g. Help moving a 3-seater sofa, 2 floors up"
                    value={data.title}
                    onChange={e => set({ title: e.target.value })}
                />
                <div className="cj-suggest">
                    <span className="mono cj-suggest-label">Try:</span>
                    {TITLE_SUGGESTIONS.slice(0, 3).map(s => (
                        <button key={s} className="cj-suggest-chip" onClick={() => set({ title: s })}>{s}</button>
                    ))}
                </div>
            </Field>

            <Field
                label="Tag it"
                sub="A few words workers might search for. Type anything — we'll suggest existing ones, or you can create new ones."
            >
                <TagInput tags={data.tags} onChange={tags => set({ tags })} />
            </Field>

            <Field label="Where" sub="Country and city — workers nearby will see this job first.">
                <div className="cj-row-2">
                    <div className="cj-input">
                        <MapPin size={16} color="var(--ink-400)" />
                        <select
                            value={data.country}
                            onChange={e => set({ country: e.target.value, city: '' })}
                            style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: '14px', color: data.country ? 'var(--ink-900)' : 'var(--ink-400)' }}
                        >
                            <option value="">Country…</option>
                            {countries.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="cj-input">
                        <select
                            value={data.city}
                            onChange={e => set({ city: e.target.value })}
                            disabled={!data.country || citiesLoading}
                            style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: '14px', color: data.city ? 'var(--ink-900)' : 'var(--ink-400)' }}
                        >
                            <option value="">
                                {!data.country ? 'Country first…' : citiesLoading ? 'Loading…' : 'City…'}
                            </option>
                            {cities.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>
                <CJInput
                    icon={MapPin}
                    placeholder="Street or area (optional)"
                    value={data.area}
                    onChange={e => set({ area: e.target.value })}
                />
            </Field>
        </div>
    );
}

function Step2({ data, set }) {
    return (
        <div className="cj-step">
            <Field label="When does it start" sub="Pick a date and the time you need them to show up.">
                <div className="cj-row-2">
                    <CJInput
                        icon={Clock}
                        type="date"
                        value={data.date}
                        onChange={e => set({ date: e.target.value })}
                    />
                    <CJInput
                        icon={Clock}
                        type="time"
                        value={data.timeStart}
                        onChange={e => set({ timeStart: e.target.value })}
                    />
                </div>
            </Field>

            <Field label="How long will it take" sub="A rough estimate helps workers know if they can fit it in.">
                <div className="cj-pills cj-pills-large">
                    {[
                        { v: '< 1 hr', l: 'Under 1 hr', sub: 'Quick errand' },
                        { v: '1–3 hrs', l: '1–3 hrs', sub: 'Short job' },
                        { v: 'Half day', l: 'Half day', sub: '~4 hours' },
                        { v: 'Full day', l: 'Full day', sub: '6–8 hours' },
                        { v: 'Multi-day', l: 'Multi-day', sub: 'Spans days' },
                    ].map(o => (
                        <button
                            key={o.v}
                            className={'cj-pill cj-pill-block' + (data.duration === o.v ? ' is-active' : '')}
                            onClick={() => set({ duration: o.v })}
                        >
                            <span className="cj-pill-l">{o.l}</span>
                            <span className="cj-pill-sub mono">{o.sub}</span>
                        </button>
                    ))}
                </div>
            </Field>

            <Field label="How flexible is the start time" sub="Wider windows get seen by more workers.">
                <div className="cj-pills">
                    {[
                        { v: 'exact', l: 'Exact time' },
                        { v: '30min', l: '± 30 min' },
                        { v: '2hr',   l: '± 2 hours' },
                        { v: 'anytime', l: 'Anytime that day' },
                    ].map(o => (
                        <button
                            key={o.v}
                            className={'cj-pill' + ((data.flex || '30min') === o.v ? ' is-active' : '')}
                            onClick={() => set({ flex: o.v })}
                        >
                            {o.l}
                        </button>
                    ))}
                </div>
            </Field>

            <div className="cj-callout">
                <Zap size={16} />
                <div>
                    <div className="cj-callout-title">Flexible jobs fill 3× faster.</div>
                    <div style={{ fontSize: '13px', opacity: 0.85 }}>
                        If you can be loose with timing, more workers will see your job.
                    </div>
                </div>
            </div>
        </div>
    );
}

function Step3({ data, set }) {
    const bench = (() => {
        for (const t of data.tags) {
            if (BENCHMARKS[t.slug]) return { ...BENCHMARKS[t.slug], tag: t.label };
        }
        return { min: 30, max: 80, tag: null };
    })();

    const benchLabel = bench.tag
        ? `Similar "${bench.tag}" jobs pay €${bench.min}–€${bench.max}`
        : `Most jobs pay €${bench.min}–€${bench.max} — add tags for a tighter estimate`;

    const updateReq = (i, v) => {
        const reqs = [...data.requirements]; reqs[i] = v; set({ requirements: reqs });
    };
    const addReq = () => set({ requirements: [...data.requirements, ''] });
    const rmReq = i => set({ requirements: data.requirements.filter((_, k) => k !== i) });

    return (
        <div className="cj-step">
            <Field label="Total payment for the job" sub="One price for the whole job. Workers see this upfront.">
                <div className="cj-pay-input">
                    <span className="cj-pay-currency">€</span>
                    <input
                        type="number"
                        placeholder="0"
                        min="1"
                        value={data.payAmount}
                        onChange={e => set({ payAmount: e.target.value })}
                    />
                </div>
                <div className="cj-bench">
                    <Info size={13} /> {benchLabel}
                </div>
            </Field>

            <Field label="Describe the job" sub="The clearer you are, the better the applicants you'll get.">
                <textarea
                    className="cj-textarea"
                    rows={5}
                    maxLength={600}
                    placeholder="What needs doing, what to expect, anything that'd help someone decide if they're a fit…"
                    value={data.description}
                    onChange={e => set({ description: e.target.value })}
                />
                <div className="cj-counter mono">{data.description.length} / 600</div>
            </Field>

            <Field label="Requirements" sub="Anything a worker should know before applying." optional>
                <div className="cj-reqs">
                    {data.requirements.map((r, i) => (
                        <div key={i} className="cj-req">
                            <Check size={14} color="var(--teal-700)" />
                            <input
                                placeholder="e.g. Must be 18+, comfortable with dogs, has own tools…"
                                value={r}
                                onChange={e => updateReq(i, e.target.value)}
                            />
                            {data.requirements.length > 1 && (
                                <button className="cj-req-rm" onClick={() => rmReq(i)} aria-label="Remove">
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    ))}
                    <button className="cj-add-btn" onClick={addReq}>+ Add requirement</button>
                </div>
            </Field>
        </div>
    );
}

function Step4({ data, set }) {
    const pay = parseFloat(data.payAmount) || 0;
    const fee = Math.round(pay * 0.08 * 100) / 100;

    return (
        <div className="cj-step">
            <div className="cj-review-banner">
                <Check size={18} />
                <div>
                    <div className="cj-review-title">Looks good. One last thing.</div>
                    <div style={{ fontSize: '13px', opacity: 0.85 }}>Choose your preferences before it goes live.</div>
                </div>
            </div>

            <div
                role="button"
                tabIndex={0}
                className="cj-switch-row"
                onClick={() => set({ verifiedOnly: !data.verifiedOnly })}
                onKeyDown={e => e.key === 'Enter' && set({ verifiedOnly: !data.verifiedOnly })}
            >
                <div>
                    <div className="cj-switch-label">Only show to verified workers</div>
                    <div className="cj-switch-sub">Smaller pool, but ID-checked applicants only.</div>
                </div>
                <Switch on={data.verifiedOnly} onChange={v => set({ verifiedOnly: v })} />
            </div>

            <div className="cj-cost">
                <div className="cj-cost-row">
                    <span>Job payment to worker</span>
                    <span className="cj-cost-val mono">€{pay.toFixed(2)}</span>
                </div>
                <div className="cj-cost-row">
                    <span>Interim platform fee (8%)</span>
                    <span className="cj-cost-val mono">
                        €{fee.toFixed(2)}{' '}
                        <span className="cj-cost-tag">First job free</span>
                    </span>
                </div>
                <div className="cj-cost-row cj-cost-total">
                    <span>Charged when accepted</span>
                    <span className="mono">€{pay.toFixed(2)}</span>
                </div>
            </div>
        </div>
    );
}

/* ─── Live preview ─── */
function LivePreview({ data }) {
    const hasContent = data.title || data.tags.length > 0 || data.city || data.area;

    return (
        <aside className="cj-preview">
            <div className="cj-preview-head">
                <span className="eyebrow">Preview</span>
                <span className="mono" style={{ fontSize: '11px', color: 'var(--ink-500)' }}>How workers will see it</span>
            </div>

            <div className="cj-preview-card">
                {!hasContent ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '280px', gap: '8px', color: 'var(--ink-500)', textAlign: 'center' }}>
                        <div style={{ fontSize: '32px', opacity: 0.3 }}>✦</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--ink-700)' }}>Your job card preview</div>
                        <div className="ix-body" style={{ fontSize: '12px', maxWidth: '24ch' }}>Start filling in Step 1 and it'll appear here.</div>
                    </div>
                ) : (
                    <>
                        <div className="cj-preview-tagline">
                            {data.tags.slice(0, 2).map(t => (
                                <span key={t.slug} className="cj-preview-tag">{t.label}</span>
                            ))}
                            {data.duration && (
                                <span className="mono" style={{ fontSize: '11px', color: 'var(--ink-500)' }}>{data.duration}</span>
                            )}
                        </div>

                        <h3 className="cj-preview-card-title">
                            {data.title || <span className="cj-preview-placeholder">Job title…</span>}
                        </h3>

                        <div className="cj-preview-facts">
                            {(data.city || data.area) && (
                                <div className="cj-preview-fact">
                                    <MapPin size={13} color="var(--ink-500)" />
                                    {[data.area, data.city, data.country].filter(Boolean).join(', ')}
                                </div>
                            )}
                            {data.date && (
                                <div className="cj-preview-fact">
                                    <Clock size={13} color="var(--ink-500)" />
                                    {data.date}{data.timeStart ? ` at ${data.timeStart}` : ''}
                                </div>
                            )}
                        </div>

                        {data.payAmount && (
                            <div className="cj-preview-pay">
                                <div className="cj-preview-pay-amt">€{data.payAmount}</div>
                                <div className="cj-preview-pay-meta mono">Fixed price · {data.duration || 'duration TBD'}</div>
                            </div>
                        )}

                        {data.description && (
                            <p className="cj-preview-desc">{data.description}</p>
                        )}

                        {data.requirements.some(r => r) && (
                            <div className="cj-preview-req">
                                <div className="cj-preview-req-title mono">Requirements</div>
                                <ul>{data.requirements.filter(r => r).map((r, i) => <li key={i}>{r}</li>)}</ul>
                            </div>
                        )}

                        <div className="cj-preview-foot">
                            <div className="cj-preview-stats">
                                <div className="cj-preview-stat">
                                    <div className="cj-preview-stat-n">11 min</div>
                                    <div className="cj-preview-stat-l mono">Avg first reply</div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </aside>
    );
}

/* ─── Steps config ─── */
const STEPS = [
    { n: 1, title: 'The basics',   sub: 'What, where, and tags' },
    { n: 2, title: 'The timing',   sub: 'When do you need it?' },
    { n: 3, title: 'Pay & details', sub: 'How much, and specifics?' },
    { n: 4, title: 'Review & post', sub: 'One last look.' },
];

const DEFAULT = {
    title: '', tags: [], area: '', country: '', city: '',
    date: '', timeStart: '', duration: '', flex: '30min',
    payAmount: '', description: '', requirements: [''],
    verifiedOnly: false,
};

function getHint(step, data) {
    if (step === 1) {
        const missing = [];
        if (!data.title) missing.push('title');
        if (!data.tags.length) missing.push('tags');
        if (!data.country) missing.push('country');
        else if (!data.city) missing.push('city');
        return missing.length ? `Add ${missing.join(' + ')}` : '';
    }
    if (step === 2) {
        const missing = [];
        if (!data.date) missing.push('date');
        if (!data.duration) missing.push('duration');
        return missing.length ? `Add ${missing.join(' + ')}` : '';
    }
    if (step === 3) {
        const missing = [];
        if (!data.payAmount) missing.push('price');
        if (!data.description) missing.push('description');
        return missing.length ? `Add ${missing.join(' + ')}` : '';
    }
    return '';
}

/* ─── Main component ─── */
const CreateJob = ({ user }) => {
    const [data, setData] = useState(DEFAULT);
    const [step, setStep] = useState(1);
    const [posted, setPosted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const set = patch => setData(d => ({ ...d, ...patch }));

    /* Pre-populate country/city from user profile */
    useEffect(() => {
        if (user?.country && !data.country) {
            set({ country: user.country, city: user.city || '' });
        }
    }, [user?.country, user?.city]);

    const canNext =
        (step === 1 && data.title && data.tags.length > 0 && data.country && data.city) ||
        (step === 2 && data.date && data.duration) ||
        (step === 3 && data.payAmount && data.description) ||
        step === 4;

    const StepComp = step === 1 ? Step1 : step === 2 ? Step2 : step === 3 ? Step3 : Step4;

    const handleSubmit = async () => {
        setSubmitting(true);
        setError('');
        try {
            const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
            if (!token) { setError('You are not logged in.'); setSubmitting(false); return; }

            const userRes = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/users/detailsbytoken`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const formData = new FormData();
            formData.append('title', data.title);
            formData.append('description', data.description);
            formData.append('address', [data.area, data.city, data.country].filter(Boolean).join(', '));
            if (data.city) formData.append('city', data.city);
            if (data.country) formData.append('country', data.country);
            formData.append('payment', data.payAmount);
            formData.append('username', userRes.data.username);
            formData.append('date', data.date);
            formData.append('timeStart', data.timeStart || '09:00');
            formData.append('tags', data.tags.map(t => t.label).join(','));
            formData.append('duration', data.duration);
            formData.append('flex', data.flex);
            formData.append('verifiedOnly', String(data.verifiedOnly));
            const reqs = data.requirements.filter(r => r.trim());
            if (reqs.length) formData.append('requirements', reqs.join('||'));

            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/jobs`,
                formData,
                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
            );

            setPosted(true);
        } catch (err) {
            console.error(err);
            setError('Failed to post job. Please try again.');
        }
        setSubmitting(false);
    };

    return (
        <div className="cj-page" style={{ background: 'var(--paper)' }}>
            <main className="cj-main">
                <div className="ix-container">

                    {/* Page header */}
                    <header className="cj-header">
                        <span className="eyebrow cj-header-eyebrow">Post a job</span>
                        <h1 className="cj-header-title">
                            Tell us what needs <span className="serif">doing.</span>
                        </h1>
                        <div className="cj-header-sub">Average time to first applicant: 11 minutes.</div>
                    </header>

                    {posted ? (
                        <div className="cj-form-card">
                            <div className="cj-success">
                                <div className="cj-success-icon"><Check size={32} /></div>
                                <h2 className="cj-success-title">Your job is <span className="serif">live.</span></h2>
                                <p className="lede" style={{ margin: '0 auto 32px', textAlign: 'center' }}>
                                    Workers can see it right now. We'll notify you when someone applies.
                                </p>
                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <button className="ix-btn ix-btn-primary ix-btn-lg" onClick={() => navigate('/my-listings')}>
                                        View my listings
                                    </button>
                                    <button
                                        className="ix-btn ix-btn-outline ix-btn-lg"
                                        onClick={() => { setPosted(false); setStep(1); setData(DEFAULT); }}
                                    >
                                        Post another
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="cj-grid">
                            <div>
                                {/* Stepper */}
                                <nav className="stepper">
                                    {STEPS.map(s => (
                                        <button
                                            key={s.n}
                                            className={
                                                'stepper-item' +
                                                (step === s.n ? ' is-active' : '') +
                                                (step > s.n ? ' is-done' : '')
                                            }
                                            onClick={() => step > s.n && setStep(s.n)}
                                            style={{ cursor: step > s.n ? 'pointer' : 'default' }}
                                        >
                                            <span className="stepper-n">
                                                {step > s.n ? <Check size={12} /> : s.n}
                                            </span>
                                            <span className="stepper-text">
                                                <span className="stepper-l">{s.title}</span>
                                                <span className="stepper-sub">{s.sub}</span>
                                            </span>
                                        </button>
                                    ))}
                                </nav>

                                {/* Form card */}
                                <div className="cj-form-card">
                                    {error && (
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: '8px',
                                            padding: '12px 16px', marginBottom: '24px',
                                            background: 'color-mix(in srgb, #ef4444 10%, transparent)',
                                            border: '1px solid color-mix(in srgb, #ef4444 30%, transparent)',
                                            borderRadius: 'var(--r-md)', color: '#b91c1c', fontSize: '14px',
                                        }}>
                                            <Info size={16} /> {error}
                                        </div>
                                    )}

                                    <StepComp data={data} set={set} />

                                    <div className="cj-form-foot">
                                        {step > 1 ? (
                                            <button className="ix-btn ix-btn-ghost" onClick={() => setStep(step - 1)}>
                                                ← Back
                                            </button>
                                        ) : <span />}

                                        <div className="cj-form-foot-right">
                                            {!canNext && step < 4 && (
                                                <span className="cj-step-hint mono">{getHint(step, data)}</span>
                                            )}
                                            <span className="cj-step-meta mono">Step {step} of 4</span>
                                            {step < 4 ? (
                                                <button
                                                    className="ix-btn ix-btn-primary"
                                                    disabled={!canNext}
                                                    onClick={() => setStep(step + 1)}
                                                    style={{ opacity: canNext ? 1 : 0.45 }}
                                                >
                                                    Continue <ArrowRight size={14} />
                                                </button>
                                            ) : (
                                                <button
                                                    className="ix-btn ix-btn-primary ix-btn-lg"
                                                    disabled={submitting}
                                                    onClick={handleSubmit}
                                                >
                                                    {submitting ? 'Posting…' : 'Post job'} <ArrowRight size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <LivePreview data={data} />
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default CreateJob;
